import React from "react";
import ProfileContainer from "./ProfileContainer";
import useInvestorEvidenceRequestStore from "../zstore/investorEvidenceRequest";
import { useShallow } from "zustand/react/shallow";
import {
  EvidenceRequestHistoryItem,
  InvestorEvidenceRequestDetail,
  InvestorEvidenceRequestOverview,
} from "../api/transformers/requiredEvidence";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import Empty from "../components/Empty";
import {
  EvidenceRequestsRelateToIDVS,
  EvidenceRequestStatusColors,
  EvidenceRequestStatusEnum,
  EvidenceRequestStatusNaming,
  NeglectableEvidenceRequestStatus,
  EvidenceRequestStatus,
} from "../constant/requiredEvidence";
import { formatDate, formatDateAndTime } from "../utils/date";
import {
  LoadingButton,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import useBoolean from "../hooks/useBoolean";
import { NavArrowDown } from "iconoir-react";
import { RqdEvidenceAPI } from "../api";
import { useSnackbar } from "notistack";
import HighlightWrapper from "./HighlightWrapper";
import useHighlightStore, { ItemType } from "../zstore/highlightStore";
import { fade } from "../utils/color";
import EvidenceRequestDetailsModal from "./EvidenceRequestDetailsModal";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { AmlStatus } from "../constant/amlConstant";
import useMessage from "../hooks/useMessage";
import { find } from "lodash";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import { IDVGroupMarkers, MarkerGroups } from "../constant/markerConstant";
import useInvestorAmlDocumentStore from "../zstore/investorAmlDocument";
import useRequiredEvidenceTypesStore from "../zstore/requiredEvidenceTypes";
import { scrollTo } from "../utils/dom";
import { usePermissionAction } from "../hooks/usePermissionAction";

type RequiredEvidenceProfileProps = {
  personId: string;
};

const RequiredEvidenceProfile: React.FC<RequiredEvidenceProfileProps> = ({
  personId,
}) => {
  const { evidenceRequests, evidenceRequestsLoading } =
    useInvestorEvidenceRequestStore(
      useShallow((state) => ({
        evidenceRequests: state.data,
        evidenceRequestsLoading: state.loading,
      }))
    );
  return (
    <ProfileContainer
      id="required-evidence-section"
      title="Required Evidence"
      loading={evidenceRequestsLoading}
    >
      <EvidenceRequestList dataSource={evidenceRequests} personId={personId} />
    </ProfileContainer>
  );
};

type EvidenceRequestListProps = {
  personId: string;
  dataSource: InvestorEvidenceRequestOverview[];
};
const EvidenceRequestList: React.FC<EvidenceRequestListProps> = ({
  personId,
  dataSource,
}) => {
  return (
    <Stack direction="column" rowGap="6px">
      {dataSource.length === 0 ? (
        <Empty />
      ) : (
        dataSource.map((el) => (
          <EvidenceRequestItem data={el} personId={personId} />
        ))
      )}
    </Stack>
  );
};

type EvidenceRequestItemProps = {
  personId: string;
  data: InvestorEvidenceRequestOverview;
};
const EvidenceRequestItem: React.FC<EvidenceRequestItemProps> = ({
  personId,
  data,
}) => {
  const checkAmlUpdatePermission = usePermissionAction("update:investor-aml");
  const message = useMessage();
  const { amlDetail, refetchAmlDetail } = useInvestorAmlDetailStore(
    useShallow((state) => ({
      amlDetail: state.data,
      refetchAmlDetail: state.fetchData,
    }))
  );
  const investorMarkers = useInvestorMarkerStore((state) => state.data);
  const fetchInvestorDocuments = useInvestorAmlDocumentStore(
    (state) => state.fetchData
  );

  // use React Query-based required evidence types hook
  const {
    data: requiredEvidenceTypes,
    loading: requiredEvidenceLoading,
    error: requiredEvidenceError,
  } = useRequiredEvidenceTypesStore(personId);

  const fetchInvestorEvidenceRequest = useInvestorEvidenceRequestStore(
    (state) => state.fetchData
  );
  const removeHighlight = useHighlightStore((state) => state.removeItem);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [
    detailLoading,
    { setTrue: startLoadingDetail, setFalse: finishLoadingDetail },
  ] = useBoolean(false);
  const [
    detailDialogOpen,
    { setTrue: showDetailDialog, setFalse: closeDetailDialog },
  ] = useBoolean(false);
  const [requestDetail, setRequestDetail] =
    React.useState<InvestorEvidenceRequestDetail | null>(null);
  const [
    historyLoading,
    { setTrue: startLoadingHistory, setFalse: finishLoadingHistory },
  ] = useBoolean(false);
  const [
    historyDialogOpen,
    { setTrue: showHistoryDialog, setFalse: closeHistoryDialog },
  ] = useBoolean(false);
  const [requestHistory, setRequestHistory] = React.useState<
    EvidenceRequestHistoryItem[]
  >([]);
  const loading = detailLoading || historyLoading;

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };
  const handleOpenDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleDetailClick = () => {
    handleCloseDropdown();
    startLoadingDetail();
    if (data.realRequest) {
      RqdEvidenceAPI.getEvidenceRequestDetail(personId, data.code)
        .then((response) => {
          setRequestDetail(response);
          showDetailDialog();
        })
        .catch((err) => {
          console.error(err);
          message.error("Failed to fetch request details.");
        })
        .finally(finishLoadingDetail);
    } else {
      // it not a real request. We know the status is NRQ and all fields blank.
      return Promise.resolve<InvestorEvidenceRequestDetail>({
        ...data,
        documents: [],
        reviewerComment: "",
        submitterComment: "",
        updatedByUser: "",
        createdByUser: "",
        updatedOnDate: "",
        updatedOnTime: "",
      })
        .then((response) => {
          setRequestDetail(response);
          showDetailDialog();
        })
        .finally(finishLoadingDetail);
    }
  };
  const handleTimelineClick = () => {
    handleCloseDropdown();
    startLoadingHistory();
    RqdEvidenceAPI.getEvidenceRequestHistory(personId, data.code)
      .then((response) => {
        setRequestHistory(response);
        showHistoryDialog();
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to fetch request history.");
      })
      .finally(finishLoadingHistory);
  };
  const handlePreconditionCheck = (
    fromStatus: EvidenceRequestStatusEnum,
    toStatus: EvidenceRequestStatusEnum
  ) => {
    // allow non-status change
    if (fromStatus === toStatus) {
      return true;
    }
    const toStatusNeglectable =
      NeglectableEvidenceRequestStatus.includes(toStatus);
    // Forbid changing required evidence status to non-final status for aml-ACT investor.
    if (amlDetail!.status === AmlStatus.ACT && !toStatusNeglectable) {
      message.info({
        content:
          "The Required Evidence status cannot be changed when the AML Status is Verified - please first update the AML Status.",
        style: {
          marginTop: "45vh",
        },
      });
      closeDetailDialog();
      return false;
    }
    // check IDVS marker before changing related required evidence status.
    if (EvidenceRequestsRelateToIDVS.includes(data.code)) {
      const idvMarker = find(investorMarkers, {
        groupCode: MarkerGroups.IDVS,
        removed: false,
      });
      // Previous logic blocked changing Identification/Address when ID&V was Passed.
      // Business update: Allow selecting Required or Further Evidence Required (and other non-final statuses)
      // even when ID&V is Passed to cater for EDD based on risk rating.
      if (
        idvMarker?.code === IDVGroupMarkers.PASS &&
        !toStatusNeglectable &&
        !([
          EvidenceRequestStatus.REQ,
          EvidenceRequestStatus.FRQ,
          EvidenceRequestStatus.NOT,
        ] as EvidenceRequestStatusEnum[]).includes(toStatus)
      ) {
        message.info({
          content:
            'This status change is restricted while ID&V Status is "Passed" (Required / Requested / Further Evidence Allowed).',
          style: {
            marginTop: "45vh",
          },
        });
        scrollTo(IDVGroupMarkers.PASS, { block: "center" });
        closeDetailDialog();
        return false;
      }
    }
    return true;
  };
  const handleUpdateSuccess = (
    status: EvidenceRequestStatusEnum,
    code: string
  ) => {
    closeDetailDialog();
    if (NeglectableEvidenceRequestStatus.includes(status)) {
      removeHighlight({
        id: code,
        type: ItemType.EVIDENCE_REQUEST,
      });
    }
    // when there's no pending evidence request, api will change the aml status.
    // so refetch aml details to keep aml status in sync.
    refetchAmlDetail(personId);

    // avoid calling API while required evidence types are still loading or errored
    if (requiredEvidenceLoading || requiredEvidenceError) {
      return;
    }

    fetchInvestorEvidenceRequest(personId, requiredEvidenceTypes || []);
    fetchInvestorDocuments(personId);
  };
  return (
    <HighlightWrapper id={data.code} type={ItemType.EVIDENCE_REQUEST}>
      {(isHighlighted) => {
        return (
          <Grid
            container
            key={data.code}
            id={data.code}
            alignItems="center"
            gap="20px"
            sx={{
              flexWrap: "wrap",
              borderLeft: `6px solid ${
                EvidenceRequestStatusColors[
                  data.status as EvidenceRequestStatusEnum
                ]
              }`,
              color: "rgba(0, 0, 0, 0.87)",
              padding: "6px",
              width: "100%",
              backgroundColor: isHighlighted
                ? fade(
                  EvidenceRequestStatusColors[
                      data.status as EvidenceRequestStatusEnum
                  ],
                  10
                )
                : "#F6F6F6",
              "&:hover": {
                boxShadow: (theme) =>
                  theme.palette.mode === "light"
                    ? "0 0px 8px rgba(0, 0, 0, 0.2)"
                    : "0 0px 8px rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <Grid item md={4} sm={6}>
              <div>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    marginBottom: "0.2rem",
                  }}
                >
                  Type:
                </Typography>
                <Typography>{data.description}</Typography>
              </div>
            </Grid>
            <Grid item md={2} sm={6}>
              <div>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    marginBottom: "0.2rem",
                  }}
                >
                  Status:
                </Typography>
                <Typography>
                  {
                    EvidenceRequestStatusNaming[
                      data.status as EvidenceRequestStatusEnum
                    ]
                  }
                </Typography>
              </div>
            </Grid>
            <Grid item md={2} sm={6}>
              <div>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    marginBottom: "0.2rem",
                  }}
                >
                  Updated:
                </Typography>
                <Typography>
                  {data.dateUpdated ? formatDate(data.dateUpdated) : "-"}
                </Typography>
              </div>
            </Grid>
            <Grid item md={2} sm={6}>
              <div>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    marginBottom: "0.2rem",
                  }}
                >
                  Updated By:
                </Typography>
                <Typography>{data.updatedBy || "-"}</Typography>
              </div>
            </Grid>
            <Grid item sx={{ textAlign: "right", maxWidth: "fit-content" }}>
              <LoadingButton
                loading={loading}
                onClick={handleOpenDropdown}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ borderRadius: "4px", padding: "3px 9px", color: "rgba(0, 0, 0, 0.87)" }}
                endIcon={<NavArrowDown />}
              >
                Actions
              </LoadingButton>
              <Menu
                id="actions-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseDropdown}
                MenuListProps={{ dense: true }}
                PaperProps={{
                  sx: { p: 0 },
                }}
              >
                <MenuItem onClick={checkAmlUpdatePermission(handleDetailClick)}>
                  Details
                </MenuItem>
                <MenuItem onClick={handleTimelineClick}>Timeline</MenuItem>
              </Menu>
            </Grid>
            <EvidenceRequestHistoryModal
              open={historyDialogOpen}
              history={requestHistory}
              onClose={closeHistoryDialog}
            />
            <EvidenceRequestDetailsModal
              personId={personId}
              open={detailDialogOpen}
              data={requestDetail}
              onCancel={closeDetailDialog}
              onSuccess={handleUpdateSuccess}
              onPreconditionCheck={handlePreconditionCheck}
            />
          </Grid>
        );
      }}
    </HighlightWrapper>
  );
};

type EvidenceRequestHistoryModalPros = {
  open: boolean;
  history: EvidenceRequestHistoryItem[];
  onClose: () => void;
};
const EvidenceRequestHistoryModal: React.FC<
  EvidenceRequestHistoryModalPros
> = ({ history, open, onClose }) => {
  const renderTimelineContent = (item: EvidenceRequestHistoryItem) => {
    return (
      <Stack
        direction="column"
        rowGap="4px"
        sx={{
          border: (theme) =>
            theme.palette.mode === "light"
              ? "1px solid #f0f0f0"
              : "1px solid #2a2a2a",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#fff" : "#121212",
          width: "100%",
        }}
      >
        <Stack direction="row" alignItems="center" columnGap="6px">
          <Chip
            sx={{
              borderRadius: "4px",
              backgroundColor:
                EvidenceRequestStatusColors[
                  item.statusApplied as EvidenceRequestStatusEnum
                ],
            }}
            label={
              EvidenceRequestStatusNaming[
                item.statusApplied as EvidenceRequestStatusEnum
              ]
            }
          />
          <Typography sx={{ fontStyle: "italic" }}>
            {formatDateAndTime(item.createdDate, item.createdTime)} By{" "}
            {item.operator || "System"}
          </Typography>
        </Stack>
        {item.description && <Typography>{item.description}</Typography>}
        {item.comments && <Typography>{item.comments}</Typography>}
      </Stack>
    );
  };
  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{
        sx: {
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.2rem" }}>Request Timeline</DialogTitle>
      <DialogContent sx={{ maxHeight: "500px" }}>
        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
        >
          {history.map((el, index) => {
            const isLast = history.length - 1 === index;
            return (
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot />
                  {isLast ? null : <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>{renderTimelineContent(el)}</TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequiredEvidenceProfile;
